import { THEME } from '@common/constants/theme.constant';
import { FieldV2, IFieldV2Props } from '@common/general-ui/field-v2/field-v2.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { SerializedStyles } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';

export type ITextAreaV2ChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

export interface ITextAreaV2Props<TName = string> extends IFieldV2Props<string, TName> {
    maxLength?: number;
    className?: string;
    onChange?: ITextAreaV2ChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

// eslint-disable-next-line react/display-name
export const TextAreaV2: React.FC<ITextAreaV2Props> = (props): JSX.Element => {
    const { placeholder, onBlur, name, maxLength } = props;

    const [val, setVal] = useState(props.defaultValue || props.value);
    useEffect(() => {
        if (props.value !== undefined) {
            setVal(props.value);
        }
    }, [props.value]);

    useEffect(() => {
        if (props.defaultValue !== undefined) {
            setVal(props.defaultValue);
        }
    }, [props.defaultValue]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;

            setVal(newValue);
            props.onChange && props.onChange(e);
        },
        [props],
    );
    return (
        <FieldV2 {...props}>
            <div tw="relative">
                {props.readOnly && <Readonly />}
                <textarea
                    tabIndex={props.readOnly ? -1 : 0}
                    readOnly={props.readOnly}
                    name={name}
                    rows={5}
                    css={styles.textArea(!!props.readOnly, !!props.errorMessage)}
                    placeholder={placeholder}
                    onChange={handleChange}
                    onBlur={onBlur}
                    maxLength={maxLength}
                    value={val}
                ></textarea>
            </div>
        </FieldV2>
    );
};

const styles = {
    textArea: (readOnly: boolean, error: boolean): (SerializedStyles | boolean)[] => [
        css`
            padding: 12px;
            height: auto;
            border: 1px solid ${THEME.GRAY_300};
            border-radius: 4px;
            font-weight: 300;
            color: ${THEME.BLACK};
            display: block;
            width: 100%;
            font-size: ${THEME.BASE_FONT_SIZE};
            box-sizing: border-box;

            :focus {
                outline-color: transparent;
                outline-style: none;
                border-color: ${THEME.TEXT_BLACK};
            }
            ::placeholder {
                color: ${THEME.TEXT_LIGHT_GRAY};
            }
            :disabled,
            :disabled:hover {
                border-color: ${THEME.GRAY_300};
                background-color: ${THEME.GRAY_100};
                color: ${THEME.GRAY};
            }
        `,
        readOnly &&
            css`
                border: 1px solid ${THEME.GRAY_300};
                :hover {
                    border: 1px solid ${THEME.GRAY_300};
                }
                :focus {
                    border: 1px solid ${THEME.GRAY_300};
                    outline-color: transparent;
                    outline-style: none;
                    box-shadow: none;
                }
                .ant-input-affix-wrapper-readonly {
                    border: 1px solid ${THEME.GRAY_300};
                }
                &&.ant-input-affix-wrapper:focus-within {
                    outline-color: transparent;
                    outline-style: none;
                    box-shadow: none;
                }
            `,
        error &&
            css`
                border-color: ${THEME.PINK};
            `,
    ],
};
